/**
 * Large-collection filter fallback — Storefront API + theme card rendering.
 *
 * Shopify disables collection.filters AND URL filter params for collections
 * with more than 5,000 products. This script:
 *
 *  1. FILTER UI   — fetches filter definitions + counts from the Storefront API
 *                   (collection-scoped, no product-count limit).
 *
 *  2. PRODUCT LIST — fetches the filtered, paginated product list from the SAME
 *                   collection-scoped Storefront API call. This is the only
 *                   source that reproduces collection membership exactly past
 *                   the 5,000-product limit (so a 5,074-product collection
 *                   returns 5,074, not the store-wide ~9,000 a /search gives).
 *
 *  3. CARD RENDER — for each product in the list, fetches the REAL theme card
 *                   HTML via the Section Rendering API:
 *                       /products/{handle}?section_id={CARD_SECTION}
 *                   so the grid matches the rest of the store exactly. Falls
 *                   back to a built-in generic card if the section fetch fails.
 *
 *  4. EVENT INTERCEPTION — capture-phase listeners prevent js-component-
 *                   facets-form from making its broken collection AJAX calls.
 *
 *  REQUIRED THEME FILE: add sections/large-collection-card.liquid (provided
 *  alongside this script) and set its name via data-facets-card-section on the
 *  facets body. The section must emit your per-product card markup.
 */
(function () {
  'use strict';

  var facetsBody = document.querySelector('[data-facets-large-collection]');
  if (!facetsBody) return;

  var collectionHandle = facetsBody.getAttribute('data-facets-large-collection');
  var clearAllText = facetsBody.getAttribute('data-clear-all') || 'Clear all';

  // Name of the theme section that renders a single product card.
  // Empty string disables theme rendering and uses the built-in generic card.
  var CARD_SECTION = facetsBody.getAttribute('data-facets-card-section') || 'large-collection-card';

  var STOREFRONT_TOKEN = 'df6667ca90c46ac40d163095f11a51fd';
  var STOREFRONT_URL = 'https://edikted.myshopify.com/api/2024-01/graphql.json';

  var PAGE_SIZE = 24; // products per Storefront API page

  var CLOSE_ICON = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.95802 8L14.8016 13.8436C15.0661 14.1081 15.0661 14.537 14.8016 14.8016C14.537 15.0661 14.1081 15.0661 13.8436 14.8016L8 8.95802L2.15643 14.8016C1.89188 15.0661 1.46296 15.0661 1.19841 14.8016C0.933863 14.537 0.933863 14.1081 1.19841 13.8436L7.04198 8L1.19841 2.15643C0.933863 1.89188 0.933863 1.46296 1.19841 1.19841C1.46296 0.933863 1.89188 0.933863 2.15643 1.19841L8 7.04198L13.8436 1.19841C14.1081 0.933863 14.537 0.933863 14.8016 1.19841C15.0661 1.46296 15.0661 1.89188 14.8016 2.15643L8.95802 8Z" fill="#252424"/></svg>';

  var GATED_LABELS = ['Rise', 'Fit', 'Fit Length', 'Stretch'];
  var JEANS_HANDLES = ['jeans','jeans-copy','baggy-jeans','barrel-jeans','bootcut-jeans','flared-jeans','skinny-jeans','wide-jeans','straight-jeans'];
  var isJeansCollection = JEANS_HANDLES.indexOf(collectionHandle.toLowerCase()) !== -1;

  var filterInputLookup = {};

  var paging = { cursor: null, hasNext: false, loading: false, token: 0 };

  // Measured height of a rendered card, used to size load-more placeholders so
  // the grid doesn't reflow when real cards replace them. Updated on each fill.
  var cardHeight = 0;

  // ─── STOREFRONT API ─────────────────────────────────────────────────────────

  var FILTERS_QUERY = [
    'query Filters($handle:String!,$filters:[ProductFilter!]){',
    '  collection(handle:$handle){',
    '    products(first:0,filters:$filters){',
    '      filters{ id label type values{ id label count input } }',
    '    }',
    '  }',
    '}'
  ].join('');

  // Lighter than before: the theme section owns the card visuals, so we only
  // need handle (+ fields the built-in fallback card uses if a section fetch
  // fails).
  var PRODUCTS_QUERY = [
    'query Products($handle:String!,$filters:[ProductFilter!],$sortKey:ProductCollectionSortKeys,$reverse:Boolean,$first:Int!,$after:String){',
    '  collection(handle:$handle){',
    '    products(first:$first,after:$after,filters:$filters,sortKey:$sortKey,reverse:$reverse){',
    '      pageInfo{ hasNextPage endCursor }',
    '      nodes{',
    '        id title handle availableForSale',
    '        featuredImage{ url altText }',
    '        images(first:2){ nodes{ url altText } }',
    '        priceRange{ minVariantPrice{ amount currencyCode } }',
    '        compareAtPriceRange{ minVariantPrice{ amount currencyCode } }',
    '      }',
    '    }',
    '  }',
    '}'
  ].join('');

  function storefrontFetch(query, variables) {
    return fetch(STOREFRONT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
      },
      body: JSON.stringify({ query: query, variables: variables || {} })
    }).then(function (r) { return r.json(); });
  }

  function mapSort(sortBy) {
    switch (sortBy) {
      case 'best-selling':       return { sortKey: 'BEST_SELLING', reverse: false };
      case 'title-ascending':    return { sortKey: 'TITLE', reverse: false };
      case 'title-descending':   return { sortKey: 'TITLE', reverse: true };
      case 'price-ascending':    return { sortKey: 'PRICE', reverse: false };
      case 'price-descending':   return { sortKey: 'PRICE', reverse: true };
      case 'created-ascending':  return { sortKey: 'CREATED', reverse: false };
      case 'created-descending': return { sortKey: 'CREATED', reverse: true };
      case 'manual':
      default:                   return { sortKey: 'COLLECTION_DEFAULT', reverse: false };
    }
  }

  function paramsToGQLFilters(params) {
    var filters = [];
    params.forEach(function (v, k) {
      if (k === 'filter.v.price.gte' || k === 'filter.v.price.lte') return;

      var lookupKey = k + '\x00' + v;
      if (filterInputLookup[lookupKey]) {
        try { filters.push(JSON.parse(filterInputLookup[lookupKey])); return; } catch (e) {}
      }

      if (k === 'filter.v.availability') {
        filters.push({ available: v === '1' });
      } else if (k.startsWith('filter.v.option.')) {
        filters.push({ variantOption: { name: k.replace('filter.v.option.', ''), value: v } });
      } else if (k === 'filter.p.product_type') {
        filters.push({ productType: v });
      } else if (k === 'filter.p.vendor') {
        filters.push({ productVendor: v });
      } else if (k.startsWith('filter.p.m.')) {
        var parts = k.replace('filter.p.m.', '').split('.');
        if (parts.length >= 2) {
          filters.push({ productMetafield: { namespace: parts[0], key: parts[1], value: v } });
        }
      }
    });
    var gte = params.get('filter.v.price.gte');
    var lte = params.get('filter.v.price.lte');
    if (gte !== null || lte !== null) {
      var p = { price: {} };
      if (gte !== null) p.price.min = parseFloat(gte);
      if (lte !== null) p.price.max = parseFloat(lte);
      filters.push(p);
    }
    return filters;
  }

  function inputToUrlValue(inputJson) {
    try {
      var o = JSON.parse(inputJson);
      if (o.available !== undefined) return o.available ? '1' : '0';
      if (o.variantOption)    return o.variantOption.value;
      if (o.productType)      return o.productType;
      if (o.productVendor)    return o.productVendor;
      if (o.productMetafield) return o.productMetafield.value;
      if (o.tag)              return o.tag;
    } catch (e) {}
    return null;
  }

  function inputToUrlParamName(inputJson) {
    try {
      var o = JSON.parse(inputJson);
      if (o.available !== undefined) return 'filter.v.availability';
      if (o.variantOption)    return 'filter.v.option.' + o.variantOption.name.toLowerCase();
      if (o.productType)      return 'filter.p.product_type';
      if (o.productVendor)    return 'filter.p.vendor';
      if (o.productMetafield) return 'filter.p.m.' + o.productMetafield.namespace + '.' + o.productMetafield.key;
      if (o.tag)              return 'filter.p.tag';
    } catch (e) {}
    return null;
  }

  function filterParamName(filter) {
    if (filter.id && filter.id.indexOf('gid://') === -1) return filter.id;
    if (filter.values && filter.values.length > 0) {
      return inputToUrlParamName(filter.values[0].input);
    }
    return null;
  }

  function esc(s) {
    return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  // Availability filter value counts partition the whole set, so summing them
  // gives the exact filtered total. Null if no availability filter exists.
  function totalFromFilters(filters) {
    var avail = null;
    filters.forEach(function (f) {
      if (filterParamName(f) === 'filter.v.availability') avail = f;
    });
    if (!avail) return null;
    var total = 0;
    avail.values.forEach(function (v) { total += (v.count || 0); });
    return total;
  }

  function setProductsCount(total) {
    if (total === null || total === undefined) return;
    document.querySelectorAll('[data-facets-form-content="products-count"]').forEach(function (el) {
      el.textContent = total + (total === 1 ? ' product' : ' products');
    });
  }

  function money(amount, currency) {
    var n = parseFloat(amount);
    if (isNaN(n)) return '';
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(n); }
    catch (e) { return n.toFixed(2); }
  }

  function imgUrl(url, w) {
    if (!url) return '';
    try { var u = new URL(url); u.searchParams.set('width', w); return u.toString(); }
    catch (e) { return url; }
  }

  // ─── RENDER FILTER HTML ─────────────────────────────────────────────────────

  function buildFilterHTML(filters, activeParams) {
    var html = '';
    filters.forEach(function (filter, fi) {
      if (GATED_LABELS.indexOf(filter.label) !== -1 && !isJeansCollection) return;

      if (filter.type === 'PRICE_RANGE') {
        html += buildPriceFilter(filter, activeParams);
        return;
      }

      var paramName = filterParamName(filter);
      if (!paramName) return;

      var isColor = paramName.indexOf('color') !== -1 || paramName.indexOf('colour') !== -1 ||
                    filter.id.indexOf('color') !== -1 || filter.id.indexOf('colour') !== -1;

      var blockId = 'lc-filter-block-' + esc(filter.label).replace(/\W+/g, '-') + '-' + fi;

      html += '<div class="facets__filter facets__filter--open" tabindex="-1"' +
        ' data-filter-name="' + esc(filter.label) + '"' +
        ' data-collection-section-accordion="facets__filter--open">' +
        '<h2 class="facet__filter-title">' +
        '<button type="button" data-collection-section-accordion-toggler aria-expanded="true" aria-controls="' + blockId + '"' +
        ' style="all: unset; display: block; width: 100%; cursor: pointer;">' + esc(filter.label) + '</button>' +
        '</h2>' +
        '<div id="' + blockId + '"><fieldset><legend class="visually-hidden">' + esc(filter.label) + '</legend><ul class="facet__filter-items">';

      filter.values.forEach(function (val, vi) {
        var urlVal = inputToUrlValue(val.input);
        if (urlVal === null) return;
        if (urlVal.indexOf('gid://') === 0) urlVal = val.label;

        filterInputLookup[paramName + '\x00' + urlVal] = val.input;

        var id = 'filter-' + filter.label.replace(/\W+/g, '-') + '-' + vi;
        var active = activeParams.getAll(paramName).indexOf(urlVal) !== -1;
        var disabled = val.count === 0 && !active;
        var isMetafield = paramName.indexOf('filter.p.m.') === 0;
        var hideItem = isMetafield && disabled;
        var isSize = paramName === 'filter.v.option.size';
        var label = isSize ? esc(val.label) : esc(val.label).toLowerCase();

        html += '<li class="facet__filter-item' + (isColor ? '' : ' form-element') + '"' +
          (hideItem ? ' style="display:none"' : '') + '>' +
          '<input type="checkbox"' +
          ' name="' + esc(paramName) + '"' +
          ' data-facets-form-input' +
          ' value="' + esc(urlVal) + '"' +
          ' id="' + esc(id) + '"' +
          (active ? ' checked' : '') +
          (disabled ? ' disabled' : '') + '>' +
          '<label for="' + esc(id) + '">' +
          (isColor ? '<span class="facet__filter-item-color filter-color-' + esc(val.label) + '" style="color:' + esc(val.label) + ';"></span>' : '') +
          '<span>' + label + '</span>' +
          '</label></li>';
      });

      html += '</ul></fieldset></div></div>';
    });
    return html;
  }

  function buildPriceFilter(filter, activeParams) {
    var gte = activeParams.get('filter.v.price.gte') || '';
    var lte = activeParams.get('filter.v.price.lte') || '';
    var active = gte || lte;
    var clearUrl = '';
    if (active) {
      var np = new URLSearchParams(activeParams);
      np.delete('filter.v.price.gte'); np.delete('filter.v.price.lte');
      clearUrl = window.location.pathname + (np.toString() ? '?' + np.toString() : '');
    }
    return '<div>' +
      '<div class="summary">' + esc(filter.label) + '</div>' +
      '<div>' +
      (active ? '<a href="' + clearUrl + '" data-facets-form-link role="button">clear</a>' : '') +
      '<div>' +
      '<div><label for="filter-Price-gte">From:</label>' +
      '<input name="filter.v.price.gte" data-facets-form-input id="filter-Price-gte"' +
      ' value="' + esc(gte) + '" type="number" placeholder="0" min="0"></div>' +
      '<div><label for="filter-Price-lte">To:</label>' +
      '<input name="filter.v.price.lte" data-facets-form-input id="filter-Price-lte"' +
      ' value="' + esc(lte) + '" type="number" min="0"></div>' +
      '</div></div></div>';
  }

  // ─── LOAD FILTER UI ─────────────────────────────────────────────────────────

  // Keeps aria-expanded on an accordion's toggler button(s) in sync with its
  // open/closed class, mirroring the logic in js-component-collection-section.
  function syncAccordionAria(accordionEl, openClass) {
    var isOpen = accordionEl.classList.contains(openClass);
    accordionEl.querySelectorAll('[data-collection-section-accordion-toggler][aria-expanded]').forEach(function (el) {
      el.setAttribute('aria-expanded', String(isOpen));
    });
  }

  function loadFilterUI() {
    var activeParams = new URLSearchParams(window.location.search);
    var dest = facetsBody.querySelector('.facet__filters');

    var accordionState = {};
    if (dest) {
      dest.querySelectorAll('[data-collection-section-accordion]').forEach(function (el) {
        var name = el.getAttribute('data-filter-name');
        if (name) accordionState[name] = el.classList.contains(el.getAttribute('data-collection-section-accordion'));
      });
    }

    storefrontFetch(FILTERS_QUERY, {
      handle: collectionHandle,
      filters: paramsToGQLFilters(activeParams)
    }).then(function (data) {
      if (!data.data || !data.data.collection) {
        console.warn('[facets-large-collection] Collection not found via Storefront API. Response:', JSON.stringify(data));
        return;
      }
      var filters = data.data.collection.products.filters;
      if (!filters || !filters.length) {
        console.warn('[facets-large-collection] Storefront API returned no filters. Ensure S&D filters are configured for this collection.');
        return;
      }

      setProductsCount(totalFromFilters(filters));

      dest = facetsBody.querySelector('.facet__filters');
      if (!dest) return;

      dest.innerHTML = buildFilterHTML(filters, activeParams);

      dest.querySelectorAll('[data-collection-section-accordion]').forEach(function (el) {
        var name = el.getAttribute('data-filter-name');
        var openClass = el.getAttribute('data-collection-section-accordion');
        if (name in accordionState) {
          accordionState[name] ? el.classList.add(openClass) : el.classList.remove(openClass);
        }
        syncAccordionAria(el, openClass);
      });

      if (window.matchMedia('(max-width: 992px)').matches) {
        dest.querySelectorAll('[data-collection-section-accordion]').forEach(function (el) {
          var openClass = el.getAttribute('data-collection-section-accordion');
          el.classList.remove(openClass);
          syncAccordionAria(el, openClass);
        });
      }

      rebuildActiveFilters();
    }).catch(function (err) {
      console.warn('[facets-large-collection] Storefront API error:', err);
    });
  }

  // ─── PRODUCT GRID ───────────────────────────────────────────────────────────

  function getGrid() {
    var productsList = document.querySelector('[data-facets-form-content="products-list"]');
    if (!productsList) return null;

    var grid = productsList.querySelector('[data-lc-grid]');
    if (!grid) {
      productsList.innerHTML =
        '<ul class="lc-product-grid" data-lc-grid></ul>' +
        '<div class="lc-sentinel" data-lc-sentinel></div>';
      grid = productsList.querySelector('[data-lc-grid]');
      injectGridStyles();
      setupSentinel(productsList.querySelector('[data-lc-sentinel]'));
    }
    return grid;
  }

  // Minimal grid layout only. The cards themselves come from the theme section,
  // so they carry their own styling; this just lays out the wrapper list. If
  // your theme card already includes its own grid-item element, you can delete
  // this and let the section's markup drive layout.
  function injectGridStyles() {
    if (document.getElementById('lc-grid-styles')) return;
    var s = document.createElement('style');
    s.id = 'lc-grid-styles';
    s.textContent = [
      '.lc-product-grid{list-style:none;margin:0;padding:0;display:grid;',
      'grid-template-columns:repeat(2,1fr);gap:16px}',
      '@media(min-width:768px){.lc-product-grid{grid-template-columns:repeat(4,1fr)}}',
      '.lc-card-slot{min-height:1px}',
      '.lc-skeleton{visibility:hidden}',
      '.lc-fallback{position:relative;font-size:13px}',
      '.lc-fallback a{text-decoration:none;color:inherit;display:block}',
      '.lc-fallback__media{position:relative;aspect-ratio:2/3;overflow:hidden;background:#f4f4f4}',
      '.lc-fallback__media img{width:100%;height:100%;object-fit:cover;display:block}',
      '.lc-fallback__title{margin:8px 0 2px;line-height:1.3}',
      '.lc-fallback__price .lc-sale{color:#c00}',
      '.lc-fallback__price .lc-was{text-decoration:line-through;opacity:.6;font-size:.9em;margin-left:6px}'
    ].join('');
    document.head.appendChild(s);
  }

  // Built-in fallback card (used only when the theme section fetch fails).
  function renderFallbackCard(p) {
    var img1 = p.featuredImage ? p.featuredImage.url : (p.images.nodes[0] ? p.images.nodes[0].url : '');
    var price = p.priceRange.minVariantPrice;
    var cmp = p.compareAtPriceRange.minVariantPrice;
    var onSale = parseFloat(cmp.amount) > parseFloat(price.amount);
    var media = '<div class="lc-fallback__media">' +
      (img1 ? '<img src="' + esc(imgUrl(img1, 400)) + '" alt="' + esc((p.featuredImage && p.featuredImage.altText) || p.title) + '" loading="lazy">' : '') +
      '</div>';
    var priceHtml = onSale
      ? '<span class="lc-sale">' + esc(money(price.amount, price.currencyCode)) + '</span>' +
        '<span class="lc-was">' + esc(money(cmp.amount, cmp.currencyCode)) + '</span>'
      : '<span>' + esc(money(price.amount, price.currencyCode)) + '</span>';
    return '<div class="lc-fallback"><a href="/products/' + esc(p.handle) + '">' + media +
      '<div class="lc-fallback__title">' + esc(p.title) + '</div>' +
      '<div class="lc-fallback__price">' + priceHtml + '</div></a></div>';
  }

  // Reveal a filled slot, drop the reserved sizing, and remember the card
  // height so later placeholders reserve the right space (no scroll reflow).
  // Only used by the infinite-scroll append path (slots are live in the grid).
  function finishSlot(el) {
    if (!el) return;
    el.classList.remove('lc-skeleton');
    el.style.minHeight = '';
    el.style.aspectRatio = '';
    var h = el.offsetHeight;
    if (h) cardHeight = h;
  }

  function createSlot(p) {
    var slot = document.createElement('li');
    slot.className = 'lc-card-slot';
    slot.setAttribute('data-handle', p.handle);
    return slot;
  }

  // Fetch the real theme card HTML for one product and place it in its slot.
  // Resolves with the slot's final element (the section may replace it
  // outright) — never rejects, so Promise.all() over many of these always
  // settles once every card has either loaded or fallen back.
  function fillSlot(slot, p, myToken) {
    if (!CARD_SECTION) { slot.innerHTML = renderFallbackCard(p); return Promise.resolve(slot); }

    return fetch('/products/' + encodeURIComponent(p.handle) + '?section_id=' + encodeURIComponent(CARD_SECTION))
      .then(function (r) { return r.text(); })
      .then(function (html) {
        // A newer filter change superseded this request; leave the slot as-is,
        // it belongs to an abandoned build and will never be shown.
        if (myToken !== paging.token) return slot;

        var trimmed = (html || '').trim();
        if (!trimmed) { slot.innerHTML = renderFallbackCard(p); return slot; }

        var tmp = document.createElement('div');
        tmp.innerHTML = trimmed;
        var first = tmp.firstElementChild;

        // If the section emits a grid item (<li>), replace the slot entirely so
        // its classes drive layout. Otherwise inject the card inside the slot.
        if (first && first.tagName === 'LI') {
          slot.replaceWith(first);
          return first;
        }
        slot.innerHTML = trimmed;
        return slot;
      })
      .catch(function () {
        if (myToken === paging.token) slot.innerHTML = renderFallbackCard(p);
        return slot;
      });
  }

  // Infinite-scroll append: adds to the bottom of an already-visible grid, so
  // progressive skeleton-fill is fine here — nothing already on screen moves.
  function appendSlots(grid, nodes, myToken) {
    nodes.forEach(function (p) {
      var slot = createSlot(p);
      slot.classList.add('lc-skeleton');
      if (cardHeight) slot.style.minHeight = cardHeight + 'px';
      else slot.style.aspectRatio = '2 / 3';
      grid.appendChild(slot);
      fillSlot(slot, p, myToken).then(finishSlot);
    });
  }

  // Build the entire new-filter result set off-screen (detached from the
  // document) and wait for every card to resolve before touching the visible
  // grid at all. The currently-shown products stay exactly as they are for
  // the whole load; the swap to the new set happens in a single step once
  // it's fully ready, instead of wiping to skeletons that fill in one by one.
  function buildAndSwapGrid(nodes, myToken, productsList) {
    if (!nodes.length) {
      if (myToken !== paging.token) return;
      var grid = getGrid();
      if (grid) grid.innerHTML = '<li class="lc-empty">No products found.</li>';
      return;
    }

    var pendingGrid = document.createElement('ul');
    var fills = nodes.map(function (p) {
      var slot = createSlot(p);
      pendingGrid.appendChild(slot);
      return fillSlot(slot, p, myToken);
    });

    Promise.all(fills).then(function () {
      // A newer filter change landed while this set was still loading —
      // discard it silently, the newer call owns the grid now.
      if (myToken !== paging.token) return;

      pendingGrid.className = 'lc-product-grid';
      pendingGrid.setAttribute('data-lc-grid', '');

      var existingGrid = productsList.querySelector('[data-lc-grid]');
      if (existingGrid) {
        existingGrid.replaceWith(pendingGrid);
      } else {
        // First takeover from the native server-rendered grid: still a single
        // swap, just now deferred until the Storefront result is fully ready.
        injectGridStyles();
        productsList.innerHTML = '';
        productsList.appendChild(pendingGrid);
        var sentinel = document.createElement('div');
        sentinel.className = 'lc-sentinel';
        sentinel.setAttribute('data-lc-sentinel', '');
        productsList.appendChild(sentinel);
        setupSentinel(sentinel);
      }
    });
  }

  function loadProducts(reset) {
    var productsList = document.querySelector('[data-facets-form-content="products-list"]');
    if (!productsList) return;

    if (reset) {
      paging.cursor = null;
      paging.hasNext = false;
      paging.token++; // invalidate in-flight pages/cards from a previous filter
    } else if (paging.loading) {
      // Only the infinite-scroll append path needs this guard against
      // double-fetching the same next page. A reset always proceeds — it's
      // already uniquely identified by its own token, and gating it on a
      // stale in-flight load would deadlock: that older call's `finally`
      // never gets to clear `paging.loading` once its token is stale.
      return;
    }
    paging.loading = true;

    var myToken = paging.token;
    var params = new URLSearchParams(window.location.search);
    var sort = mapSort(params.get('sort_by'));

    storefrontFetch(PRODUCTS_QUERY, {
      handle: collectionHandle,
      filters: paramsToGQLFilters(params),
      sortKey: sort.sortKey,
      reverse: sort.reverse,
      first: PAGE_SIZE,
      after: paging.cursor
    }).then(function (data) {
      if (myToken !== paging.token) return;

      if (!data.data || !data.data.collection) {
        console.warn('[facets-large-collection] Products query returned no collection.', JSON.stringify(data));
        return;
      }
      var conn = data.data.collection.products;
      paging.cursor = conn.pageInfo.endCursor;
      paging.hasNext = conn.pageInfo.hasNextPage;

      if (reset) {
        buildAndSwapGrid(conn.nodes, myToken, productsList);
        return;
      }

      var grid = getGrid();
      if (!grid) return;
      appendSlots(grid, conn.nodes, myToken);
    }).catch(function (err) {
      console.warn('[facets-large-collection] Product fetch error:', err);
    }).finally(function () {
      if (myToken === paging.token) paging.loading = false;
    });
  }

  var sentinelObserver = null;
  function setupSentinel(sentinel) {
    if (!sentinel || sentinelObserver) return;
    sentinelObserver = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting && paging.hasNext && !paging.loading) {
        loadProducts(false);
      }
    }, { rootMargin: '1200px' });
    sentinelObserver.observe(sentinel);
  }

  // ─── ACTIVE FILTER CHIPS ────────────────────────────────────────────────────

  function rebuildActiveFilters() {
    var zone = facetsBody.querySelector('[data-facets-form-content="active-filters"]');
    if (!zone) return;

    var params = new URLSearchParams(window.location.search);
    var entries = [];
    params.forEach(function (v, k) { if (k.startsWith('filter.')) entries.push([k, v]); });

    if (!entries.length) { zone.innerHTML = ''; return; }

    var colPath = window.location.pathname;
    var html = '';

    var gte = params.get('filter.v.price.gte');
    var lte = params.get('filter.v.price.lte');
    if (gte !== null || lte !== null) {
      var np = new URLSearchParams(entries.filter(function (e) {
        return e[0] !== 'filter.v.price.gte' && e[0] !== 'filter.v.price.lte';
      }).concat(Array.from(params.entries()).filter(function (e) { return !e[0].startsWith('filter.'); })));
      html += chip(colPath + (np.toString() ? '?' + np : ''), (gte || '0') + ' — ' + (lte || ''), false);
    }

    entries.forEach(function (entry) {
      var k = entry[0], v = entry[1];
      if (k === 'filter.v.price.gte' || k === 'filter.v.price.lte') return;
      var remaining = entries.filter(function (e) { return !(e[0] === k && e[1] === v); });
      var nonFilter = Array.from(params.entries()).filter(function (e) { return !e[0].startsWith('filter.'); });
      var np = new URLSearchParams(remaining.concat(nonFilter));
      var isColor = k.indexOf('color') !== -1 || k.indexOf('colour') !== -1;
      html += chip(colPath + (np.toString() ? '?' + np : ''), v.toLowerCase(), isColor);
    });

    var clearNp = new URLSearchParams(Array.from(params.entries()).filter(function (e) { return !e[0].startsWith('filter.'); }));
    var clearUrl = colPath + (clearNp.toString() ? '?' + clearNp : '');
    html += '<a class="facet__active-filters-reset desktop-visible" href="' + clearUrl + '" data-facets-form-link role="button">' + clearAllText + '</a>';

    zone.innerHTML = html;
  }

  function chip(removeUrl, label, isColor) {
    var inner = isColor
      ? '<span class="facet__active-filter-color">' + esc(label) + '</span>'
      : '<span>' + esc(label) + '</span>';
    return '<div class="facet__active-filter"><a href="' + removeUrl + '" data-facets-form-link role="button" class="facet__active-filter-remove" aria-label="Remove ' + esc(label) + ' filter">' + inner + '<span aria-hidden="true">' + CLOSE_ICON + '</span></a></div>';
  }

  // ─── INTERCEPT FILTER / SORT CHANGES ────────────────────────────────────────

  document.body.addEventListener('change', function (e) {
    if (!e.target.hasAttribute('data-facets-form-input')) return;
    var form = e.target.form;
    if (!form || form.id !== 'facets-form') return;
    if (!document.querySelector('[data-facets-large-collection]')) return;

    e.stopImmediatePropagation();

    var params = new URLSearchParams(new FormData(form));
    var newUrl = new URL(window.location.pathname, window.location.origin);
    params.forEach(function (v, k) {
      if (k.startsWith('filter.') || k === 'sort_by') newUrl.searchParams.append(k, v);
    });
    history.pushState({}, '', newUrl.toString());

    loadProducts(true);
    loadFilterUI();
    rebuildActiveFilters();
  }, { capture: true });

  // ─── INTERCEPT LINK CLICKS ──────────────────────────────────────────────────

  document.addEventListener('click', function (e) {
    if (!document.querySelector('[data-facets-large-collection]')) return;
    var link = e.target.closest('[data-facets-form-link]');
    if (!link || !link.href) return;

    e.stopImmediatePropagation();
    e.preventDefault();

    var linkUrl = new URL(link.href);
    var newUrl = new URL(window.location.pathname, window.location.origin);
    linkUrl.searchParams.forEach(function (v, k) {
      if (k.startsWith('filter.') || k === 'sort_by') newUrl.searchParams.append(k, v);
    });
    history.pushState({}, '', newUrl.toString());

    loadProducts(true);
    loadFilterUI();
    rebuildActiveFilters();
  }, { capture: true });

  // ─── ACCORDION TOGGLE ───────────────────────────────────────────────────────

  facetsBody.addEventListener('click', function (e) {
    var toggler = e.target.closest('[data-collection-section-accordion-toggler]');
    if (!toggler) return;
    var accordion = toggler.closest('[data-collection-section-accordion]');
    if (!accordion) return;
    var openClass = accordion.getAttribute('data-collection-section-accordion');
    accordion.classList.toggle(openClass);
    syncAccordionAria(accordion, openClass);
  });

  // ─── BROWSER HISTORY ────────────────────────────────────────────────────────

  window.addEventListener('popstate', function () {
    loadFilterUI();
    loadProducts(true);
  });

  // ─── INIT ────────────────────────────────────────────────────────────────────

  loadFilterUI();

  // Only take over the product grid if the page already has filters/sort in the
  // URL (e.g. a shared filtered link). On a plain collection open, the native
  // server-rendered grid is already correct (membership is fine when nothing is
  // filtered), so we leave it untouched — no clear-and-reload flash. We hand off
  // to the API path the moment the shopper interacts (the change/click handlers).
  (function () {
    var p = new URLSearchParams(window.location.search);
    var takeover = false;
    p.forEach(function (v, k) { if (k.indexOf('filter.') === 0 || k === 'sort_by') takeover = true; });
    if (takeover) loadProducts(true);
  })();

})();